import { decodeJwtPayload } from '../../../custom-ui/iframe/utils/tokenComparison';
import { fg } from '@atlaskit/platform-feature-flags';

export type RealtimeChannelPermission = 'subscribe' | 'publish';

export interface RealtimeChannelTokenClaims {
	accountId: string;
	channel: {
		context: Record<string, unknown>;
		name: string;
	};
	/** JWT expiry */
	exp?: number;
	installationId: string;
	permissions?: Array<RealtimeChannelPermission> | null;
}

export type RealtimeTokenValidationError =
	| 'INVALID_TOKEN'
	| 'TOKEN_EXPIRED'
	| 'MISSING_PERMISSION'
	| 'CHANNEL_NAME_MISMATCH';

export interface RealtimeTokenValidationResult {
	error?: RealtimeTokenValidationError;
	valid: boolean;
}

export interface ValidateRealtimeChannelTokenOptions {
	channelName: string;
	requiredPermission: RealtimeChannelPermission;
}

/**
 * Decodes and validates an ASAP-signed Forge Realtime Token (FRT) without verifying
 * the signature (signature verification happens server-side). This is a
 * pre-flight check only — it prevents obviously invalid requests from being
 * dispatched and surfaces errors early.
 *
 * Checks performed:
 *  - Token is valid
 *  - Token has not expired
 *  - Token grants the required permission
 *  - channel.name matches the intended channel
 */
export function validateRealtimeChannelToken(
	token: string,
	options: ValidateRealtimeChannelTokenOptions,
): RealtimeTokenValidationResult {
	const decoded = decodeJwtPayload(token) as RealtimeChannelTokenClaims | undefined;

	if (!decoded) {
		return { valid: false, error: 'INVALID_TOKEN' };
	}

	const { channelName, requiredPermission } = options;
	const { exp, channel, permissions } = decoded;

	if (
		exp !== undefined &&
		Date.now() / 1000 >= exp &&
		fg('forge-ui-realtime-pre-validate-frt-existing-fields')
	) {
		return { valid: false, error: 'TOKEN_EXPIRED' };
	}

	if (
		channel.name &&
		channel.name !== channelName &&
		fg('forge-ui-realtime-pre-validate-frt-existing-fields')
	) {
		return { valid: false, error: 'CHANNEL_NAME_MISMATCH' };
	}

	if (permissions && !permissions.includes(requiredPermission)) {
		return { valid: false, error: 'MISSING_PERMISSION' };
	}

	return { valid: true };
}
